import { SyntheticEvent } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { useRecoilCleanup } from 'hooks/useRecoilCleanup';
import PromptDialog from 'components/common/Dialog/PromptDialog';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { getStringFromError } from 'components/common/CodeEditor/helpers';
import { activeTeamState } from 'components/account/account.state';
import { UPDATE_TEAM_NAME } from 'components/teamAdministration/TeamAdministrationPage.queries';

type UpdateTeamNameDialogLocalState = {
  showEditTeamNameDialog: boolean;
  teamNameDraft: string;
};

export const updateTeamNameDialogLocalState = atom<UpdateTeamNameDialogLocalState>({
  key: K.RECOIL_ATOM_KEY.UPDATE_TEAM_NAME_DIALOG,
  default: {
    showEditTeamNameDialog: false,
    teamNameDraft: '',
  },
});

// Client does not allow the user to submit empty string
export const getTeamNameInputError = (input: string): string | null => {
  if (!input) {
    return 'Please enter a valid team name';
  }
  return null;
};

/**
 * This hook returns the function to call when submitting this dialog.
 */
const useUpdateTeamName = () => {
  const localState = useRecoilValue(updateTeamNameDialogLocalState);
  const activeTeam = useRecoilValue(activeTeamState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 1.5,
    });
  };
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.ERROR, autoHide: true });
  };

  // If the API call is successful, Apollo cache will update the team name value for AccountData.ActiveTeam, but not for
  // the active team in the AccountData.Teams list.
  const onCompleted = (): void => {
    showSuccessAlert('Team name updated');
  };

  const onError = (error: ApolloError): void => {
    showErrorAlert(getStringFromError(error));
  };

  // change the team name
  const [changeTeamName] = useMutation(UPDATE_TEAM_NAME, { onCompleted, onError });
  return (): void => {
    changeTeamName({ variables: { name: localState.teamNameDraft, teamId: activeTeam.id } });
  };
};

/**
 * Returns a {@see PromptDialog} that allows the user to update the team name for the currently active team.
 */
function UpdateTeamNameDialog(): JSX.Element {
  const [localState, setLocalState] = useRecoilState(updateTeamNameDialogLocalState);
  const updateTeamName = useUpdateTeamName();
  useRecoilCleanup(updateTeamNameDialogLocalState);

  const handleChange = (ev: SyntheticEvent): void => {
    const eventTarget = ev.target as HTMLInputElement;
    setLocalState({ ...localState, teamNameDraft: eventTarget.value });
  };

  return (
    <PromptDialog
      title="Update Team Name"
      label="Team name"
      inline={false}
      show={localState.showEditTeamNameDialog}
      close={(): void => setLocalState({ ...localState, showEditTeamNameDialog: false })}
      value={localState.teamNameDraft}
      getError={getTeamNameInputError}
      onChange={handleChange}
      onSubmit={updateTeamName}
      testId={ELEMENT.updateTeamNameDialog}
    />
  );
}

export default UpdateTeamNameDialog;
